import { useState } from 'react';

const faqs = [
  {
    question: 'What is green hydrogen and how is it produced in Australia?',
    answer: "Green hydrogen is hydrogen gas produced by splitting water (H₂O) into hydrogen and oxygen using an electrolyser powered entirely by renewable electricity — such as solar or wind. Because no fossil fuels are used, the process emits zero carbon dioxide. Australia is developing green hydrogen projects across WA, SA, QLD, VIC, and NSW, leveraging some of the world's best solar and wind resources to produce hydrogen for domestic use and export.",
  },
  {
    question: 'What hydrogen energy solutions are available in Australia?',
    answer: "Australia's hydrogen sector offers solutions across the full value chain: green hydrogen production via electrolysis, hydrogen storage and transport (compressed gas, liquid hydrogen, ammonia carriers), hydrogen refuelling infrastructure for heavy vehicles, green ammonia production and export, power-to-gas storage for grid balancing, hydrogen for industrial heat processes, and Corporate Hydrogen Offtake Agreements for businesses seeking certified green hydrogen supply.",
  },
  {
    question: 'How do I find trusted hydrogen energy providers in Australia?',
    answer: "AK Energy connects businesses, investors, and project developers with verified hydrogen energy providers across Australia. This includes electrolyser manufacturers, hydrogen project developers, green ammonia producers, hydrogen refuelling infrastructure companies, and certified hydrogen offtake suppliers. All providers in the AK Energy marketplace are vetted for relevant qualifications, project track record, and financial standing.",
  },
  {
    question: 'Is hydrogen energy suitable for Australian businesses?',
    answer: "Yes — Australian businesses can access hydrogen energy solutions in several ways: purchasing certified green hydrogen or green ammonia for industrial processes, signing Corporate Hydrogen Offtake Agreements for long-term supply, investing in or co-developing hydrogen projects, switching heavy vehicle fleets to hydrogen fuel cells, or using hydrogen for high-temperature industrial heat. The government's Hydrogen Headstart production incentive also creates opportunities for businesses to participate in early-stage green hydrogen projects.",
  },
  {
    question: "What is Australia's National Hydrogen Strategy?",
    answer: "Australia's National Hydrogen Strategy (2019, updated 2023) sets out the vision for Australia to become a major global hydrogen producer and exporter. Key targets include producing hydrogen at $2/kg (the 'H2X' target) by 2030, developing hydrogen hubs in key regions, and exporting hydrogen to Japan, South Korea, and Germany under bilateral agreements. The strategy is supported by the $2B Hydrogen Headstart production incentive, ARENA grants, and CEFC financing.",
  },
  {
    question: 'What is the difference between green, blue, and grey hydrogen?',
    answer: "The 'colour' of hydrogen describes how it was produced. Grey hydrogen is made from natural gas without carbon capture and emits 9–12 kg CO₂/kg H₂ — it accounts for 95% of current global production. Blue hydrogen is also made from natural gas but with carbon capture and storage (CCS), reducing emissions by 85–95% to 0.5–4 kg CO₂/kg H₂. Green hydrogen is produced via water electrolysis powered by renewable energy, emitting zero CO₂. Australia focuses almost exclusively on green hydrogen for both export and domestic application.",
  },
  {
    question: 'How much does green hydrogen cost in Australia?',
    answer: "Green hydrogen currently costs approximately $5–$8 per kg to produce in Australia — higher than grey hydrogen ($1–$2/kg) due to electrolyser capital costs and renewable electricity costs. The Australian government has set a target of reaching $2/kg by 2030 through electrolyser cost reductions, economies of scale, and declining renewable energy costs. This 'H2X' target would make Australian green hydrogen cost-competitive with fossil fuels for most industrial applications.",
  },
  {
    question: 'What is the Hydrogen Energy Supply Chain (HESC) project?',
    answer: "The Hydrogen Energy Supply Chain (HESC) project is a joint Australian-Japanese initiative that produced and exported the world's first liquefied hydrogen to Japan in 2022 — a historic milestone in the global hydrogen trade. The project, led by Kawasaki Heavy Industries and AGL Energy, extracted hydrogen from Latrobe Valley coal using carbon capture. A commercial-scale green hydrogen version is in development, aimed at establishing a regular hydrogen supply chain between Australia and Japan.",
  },
];

export default function HydrogenFAQ() {
  const [openIdx, setOpenIdx] = useState<number | null>(0);

  const toggle = (idx: number) => setOpenIdx(openIdx === idx ? null : idx);

  const faqSchema = {
    '@context': 'https://schema.org',
    '@type': 'FAQPage',
    mainEntity: faqs.map((f) => ({
      '@type': 'Question',
      name: f.question,
      acceptedAnswer: { '@type': 'Answer', text: f.answer },
    })),
  };

  return (
    <section className="bg-background-100/50 py-20 md:py-28">
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(faqSchema) }}
      />
      <div className="max-w-7xl mx-auto px-4 md:px-6 lg:px-10">
        <div className="grid lg:grid-cols-[1fr_2fr] gap-12 lg:gap-16 items-start">
          {/* Left */}
          <div className="lg:sticky lg:top-28">
            <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-accent-50 border border-accent-200/60 mb-5">
              <i className="ri-question-answer-line text-accent-700 text-sm"></i>
              <span className="text-xs font-semibold text-accent-800 tracking-wide uppercase whitespace-nowrap">FAQ</span>
            </div>
            <h2 className="font-heading text-3xl md:text-4xl font-extrabold text-foreground-950 mb-4">
              Hydrogen Energy<br />Questions<br />Answered
            </h2>
            <p className="text-base text-foreground-600 leading-relaxed mb-8">
              Everything you need to know about green hydrogen — from production methods and costs to Australia&rsquo;s export strategy and business opportunities.
            </p>
            <div className="space-y-3 mb-8">
              {[
                { href: '/solar-energy-australia', label: 'Solar Energy Solutions Australia', icon: 'ri-sun-line' },
                { href: '/wind-energy-solutions', label: 'Wind Energy Solutions Australia', icon: 'ri-windy-line' },
              ].map((link) => (
                <div key={link.href} className="flex items-center gap-3">
                  <div className="w-8 h-8 rounded-lg bg-background-100 flex items-center justify-center shrink-0">
                    <i className={`${link.icon} text-foreground-500 text-sm`}></i>
                  </div>
                  <a href={link.href} className="text-sm text-primary-600 hover:text-primary-700 font-medium transition-colors">
                    {link.label} →
                  </a>
                </div>
              ))}
            </div>
            <a
              href="/#contact"
              className="whitespace-nowrap inline-flex items-center gap-2 px-5 py-3 text-sm font-semibold rounded-xl border-2 border-foreground-200 text-foreground-800 hover:border-foreground-400 hover:bg-foreground-50 transition-all duration-200 cursor-pointer"
            >
              Ask a Question
              <i className="ri-arrow-right-line"></i>
            </a>
          </div>

          {/* FAQ list */}
          <div className="space-y-3">
            {faqs.map((faq, idx) => (
              <div
                key={idx}
                className={`rounded-xl border transition-all duration-200 overflow-hidden ${
                  openIdx === idx
                    ? 'bg-accent-50 border-accent-200'
                    : 'bg-background-50 border-background-200/70 hover:border-background-300'
                }`}
              >
                <button
                  onClick={() => toggle(idx)}
                  className="w-full flex items-center justify-between gap-4 px-5 py-4 text-left cursor-pointer"
                >
                  <span className={`text-sm font-semibold leading-snug ${openIdx === idx ? 'text-accent-800' : 'text-foreground-900'}`}>
                    {faq.question}
                  </span>
                  <div className={`w-7 h-7 rounded-lg flex items-center justify-center shrink-0 transition-all duration-200 ${
                    openIdx === idx ? 'bg-accent-500 text-white' : 'bg-background-200 text-foreground-600'
                  }`}>
                    <i className={`text-sm ${openIdx === idx ? 'ri-subtract-line' : 'ri-add-line'}`}></i>
                  </div>
                </button>
                {openIdx === idx && (
                  <div className="px-5 pb-5">
                    <p className="text-sm text-foreground-600 leading-relaxed">{faq.answer}</p>
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}